"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
    FiCalendar,
    FiList,
    FiPlus,
    FiEdit,
    FiTrash2,
    FiX,
    FiChevronLeft,
    FiChevronRight,
    FiFilter,
    FiUpload,
    FiAlertCircle,
} from "react-icons/fi"
import { FaSort, FaSortUp, FaSortDown, FaFileExcel, FaSpinner } from "react-icons/fa"
import axiosInstance from "../../libs/axios"
// Imported PaginationUtils
import { PaginationType, Pagination as PaginationUtils } from "../../utils/pagination.utils"
import { CloseModal } from "../sites/closemodal"
import { ExcelDownload } from "../../utils/excel.utils"
import { toast } from "react-hot-toast"
import { SearchableSelect } from "../../utils/select-with-search"

interface Holiday {
    id: number
    holiday_date: string
    holiday_name: string
    holiday_type: string
    description: string | null
    is_nationwide: boolean
    state_circle_id: number | null
    district_id: number | null
    department_id?: number | null
    // Departments the holiday applies to; empty = every department.
    department_ids?: number[]
    departments?: { id: number; name: string }[]
    is_active: boolean
    year: number
    created_by: number
    updated_by: number | null
    created_at: string
    updated_at: string
    state_circle?: {
        id: number
        state_name: string
        circle_name: string
    } | null
    district?: {
        id: number
        district_name: string
    } | null
    creator?: {
        id: number
        first_name: string
        last_name: string
    }
}

interface CalendarDay {
    date: Date
    isCurrentMonth: boolean
    holidays: Holiday[]
}

interface StateCircle {
    id: number
    state_name: string
    circle_name: string
}

interface District {
    id: number
    district_name: string
    state_circle_id: number
}

interface Department {
    id: number
    name: string
}

interface HolidaySortConfig {
    field: string
    direction: "ASC" | "DESC"
}

type HolidayExportRow = Record<string, string | number | boolean>

const departmentNames = (holiday: Holiday) =>
    holiday.departments?.length ? holiday.departments.map((d) => d.name).join(", ") : "All Departments"

// Multi-select of departments with "Select all visible". Empty = every department.
const DepartmentMultiSelect: React.FC<{
    departments: Department[]
    value: number[]
    onChange: (ids: number[]) => void
    placeholder?: string
    hint?: boolean
}> = ({ departments, value, onChange, placeholder = "All Departments", hint = true }) => {
    const options = departments.map((d) => ({ value: d.id, label: d.name }))
    return (
        <div>
            <SearchableSelect
                value={options.filter((o) => value.includes(Number(o.value)))}
                onChange={(selected: { value: number | string }[] = []) => onChange(selected.map((o) => Number(o.value)))}
                options={options}
                placeholder={placeholder}
                isMulti
                showSelectAllVisible
                portalMenu
                emptyMessage="No departments found"
            />
            {hint && (
                <p className="mt-1 text-xs text-gray-500">Leave empty to apply the holiday to all departments.</p>
            )}
        </div>
    )
}

// Add Holiday Offcanvas
const AddHolidayOffcanvas: React.FC<{
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    stateCircles: StateCircle[]
    districts: District[]
    departments: Department[]
}> = ({ isOpen, onClose, onSuccess, stateCircles, districts, departments }) => {
    const [formData, setFormData] = useState({
        holiday_date: "",
        holiday_name: "",
        holiday_type: "public_holiday",
        description: "",
        is_nationwide: true,
        state_circle_id: "",
        district_id: "",
    })
    const [departmentIds, setDepartmentIds] = useState<number[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [showCloseModal, setShowCloseModal] = useState(false)

    useEffect(() => {
        if (!isOpen) {
            setShowCloseModal(false)
        }
    }, [isOpen])

    const resetForm = () => {
        setFormData({
            holiday_date: "",
            holiday_name: "",
            holiday_type: "public_holiday",
            description: "",
            is_nationwide: true,
            state_circle_id: "",
            district_id: "",
        })
        setDepartmentIds([])
        setError("")
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        try {
            setLoading(true)
            setError("")

            const payload: any = {
                holiday_date: formData.holiday_date,
                holiday_name: formData.holiday_name,
                holiday_type: formData.holiday_type,
                description: formData.description || undefined,
                is_nationwide: departmentIds.length ? false : formData.is_nationwide,
            }

            if (!formData.is_nationwide) {
                if (formData.state_circle_id) {
                    payload.state_circle_id = Number.parseInt(formData.state_circle_id)
                }
                if (formData.district_id) {
                    payload.district_id = Number.parseInt(formData.district_id)
                }
            }
            if (departmentIds.length) {
                payload.department_ids = departmentIds
            }

            await axiosInstance.post("/calendar/holiday", payload)
            onSuccess()
            onClose()
            resetForm()
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to add holiday")
        } finally {
            setLoading(false)
        }
    }

    const filteredDistricts = formData.state_circle_id
        ? districts.filter((d) => d.state_circle_id === Number.parseInt(formData.state_circle_id))
        : []

    const requestClose = () => {
        setShowCloseModal(true)
    }

    const confirmClose = () => {
        setShowCloseModal(false)
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={requestClose}></div>
            <div className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-white shadow-xl overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                    <h2 className="text-lg font-semibold text-gray-900">Add New Holiday</h2>
                    <button onClick={requestClose} className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <FiX className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="p-4">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
                            <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-red-700">{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
                            <input
                                type="text"
                                value={formData.holiday_name}
                                onChange={(e) => setFormData({ ...formData, holiday_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="e.g., Independence Day"
                                required
                                minLength={3}
                                maxLength={200}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Date *</label>
                            <input
                                type="date"
                                value={formData.holiday_date}
                                onChange={(e) => setFormData({ ...formData, holiday_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Type *</label>
                            <select
                                value={formData.holiday_type}
                                onChange={(e) => setFormData({ ...formData, holiday_type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                required
                            >
                                <option value="public_holiday">Public Holiday</option>
                                <option value="festival">Festival</option>
                                <option value="company_holiday">Company Holiday</option>
                                <option value="optional_holiday">Optional Holiday</option>
                                <option value="restricted_holiday">Restricted Holiday</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                placeholder="Add description..."
                                maxLength={1000}
                            />
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Departments</label>
                                <DepartmentMultiSelect departments={departments} value={departmentIds} onChange={setDepartmentIds} />
                            </div>
                            {/* <div className="flex items-center mb-3">
                                <input
                                    type="checkbox"
                                    id="is_nationwide"
                                    checked={formData.is_nationwide}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        is_nationwide: e.target.checked,
                                        state_circle_id: '',
                                        district_id: ''
                                    })}
                                    className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="is_nationwide" className="ml-2 text-sm font-medium text-gray-700">
                                    Nationwide Holiday
                                </label>
                            </div> */}

                            {!formData.is_nationwide && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State/Circle</label>
                                        <select
                                            value={formData.state_circle_id}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    state_circle_id: e.target.value,
                                                    district_id: "",
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                        >
                                            <option value="">Select State/Circle</option>
                                            {stateCircles.map((sc) => (
                                                <option key={sc.id} value={sc.id}>
                                                    {sc.state_name} - {sc.circle_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {formData.state_circle_id && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">District (Optional)</label>
                                            <select
                                                value={formData.district_id}
                                                onChange={(e) => setFormData({ ...formData, district_id: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                            >
                                                <option value="">Select District</option>
                                                {filteredDistricts.map((d) => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.district_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 transition-colors text-sm font-medium disabled:opacity-50"
                            >
                                {loading ? "Adding..." : "Add Holiday"}
                            </button>
                            <button
                                type="button"
                                onClick={requestClose}
                                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <CloseModal
                isOpen={showCloseModal}
                onClose={() => setShowCloseModal(false)}
                onConfirm={confirmClose}
            />
        </div>
    )
}

// Edit Holiday Offcanvas
const EditHolidayOffcanvas: React.FC<{
    isOpen: boolean
    onClose: () => void
    holiday: Holiday | null
    onSuccess: () => void
    stateCircles: StateCircle[]
    districts: District[]
    departments: Department[]
}> = ({ isOpen, onClose, holiday, onSuccess, stateCircles, districts, departments }) => {
    const [formData, setFormData] = useState({
        holiday_date: "",
        holiday_name: "",
        holiday_type: "public_holiday",
        description: "",
        is_nationwide: true,
        state_circle_id: "",
        district_id: "",
        is_active: true,
    })
    const [departmentIds, setDepartmentIds] = useState<number[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        if (holiday) {
            setDepartmentIds(
                holiday.department_ids?.length
                    ? holiday.department_ids
                    : holiday.department_id
                        ? [holiday.department_id]
                        : []
            )
            setFormData({
                holiday_date: holiday.holiday_date,
                holiday_name: holiday.holiday_name,
                holiday_type: holiday.holiday_type,
                description: holiday.description || "",
                is_nationwide: holiday.is_nationwide,
                state_circle_id: holiday.state_circle_id?.toString() || "",
                district_id: holiday.district_id?.toString() || "",
                is_active: holiday.is_active,
            })
        }
    }, [holiday])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!holiday) return

        try {
            setLoading(true)
            setError("")

            const payload: any = {
                holiday_date: formData.holiday_date,
                holiday_name: formData.holiday_name,
                holiday_type: formData.holiday_type,
                description: formData.description || undefined,
                is_nationwide: departmentIds.length ? false : formData.is_nationwide,
                is_active: formData.is_active,
                // Always sent so clearing every department makes it apply to all again.
                department_ids: departmentIds,
            }

            if (!formData.is_nationwide) {
                if (formData.state_circle_id) {
                    payload.state_circle_id = Number.parseInt(formData.state_circle_id)
                }
                if (formData.district_id) {
                    payload.district_id = Number.parseInt(formData.district_id)
                }
            }

            await axiosInstance.put(`/calendar/holiday/${holiday.id}`, payload)
            onSuccess()
            onClose()
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to update holiday")
        } finally {
            setLoading(false)
        }
    }

    const filteredDistricts = formData.state_circle_id
        ? districts.filter((d) => d.state_circle_id === Number.parseInt(formData.state_circle_id))
        : []

    if (!isOpen || !holiday) return null

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
            <div className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-white shadow-xl overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                    <h2 className="text-lg font-semibold text-gray-900">Edit Holiday</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <FiX className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="p-4">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
                            <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-red-700">{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
                            <input
                                type="text"
                                value={formData.holiday_name}
                                onChange={(e) => setFormData({ ...formData, holiday_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                required
                                minLength={3}
                                maxLength={200}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Date *</label>
                            <input
                                type="date"
                                value={formData.holiday_date}
                                onChange={(e) => setFormData({ ...formData, holiday_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Type *</label>
                            <select
                                value={formData.holiday_type}
                                onChange={(e) => setFormData({ ...formData, holiday_type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                required
                            >
                                <option value="public_holiday">Public Holiday</option>
                                <option value="festival">Festival</option>
                                <option value="company_holiday">Company Holiday</option>
                                <option value="optional_holiday">Optional Holiday</option>
                                <option value="restricted_holiday">Restricted Holiday</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                placeholder="Add description..."
                                maxLength={1000}
                            />
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                            <div className="flex items-center mb-3">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                                    Active
                                </label>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Departments</label>
                                <DepartmentMultiSelect departments={departments} value={departmentIds} onChange={setDepartmentIds} />
                            </div>

                            <div className="flex items-center mb-3">
                                {/* <input
                                    type="checkbox"
                                    id="is_nationwide_edit"
                                    checked={formData.is_nationwide}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        is_nationwide: e.target.checked,
                                        state_circle_id: '',
                                        district_id: ''
                                    })}
                                    className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="is_nationwide_edit" className="ml-2 text-sm font-medium text-gray-700">
                                    Nationwide Holiday
                                </label> */}
                            </div>

                            {!formData.is_nationwide && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State/Circle</label>
                                        <select
                                            value={formData.state_circle_id}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    state_circle_id: e.target.value,
                                                    district_id: "",
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                        >
                                            <option value="">Select State/Circle</option>
                                            {stateCircles.map((sc) => (
                                                <option key={sc.id} value={sc.id}>
                                                    {sc.state_name} - {sc.circle_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {formData.state_circle_id && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">District (Optional)</label>
                                            <select
                                                value={formData.district_id}
                                                onChange={(e) => setFormData({ ...formData, district_id: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                            >
                                                <option value="">Select District</option>
                                                {filteredDistricts.map((d) => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.district_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 transition-colors text-sm font-medium disabled:opacity-50"
                            >
                                {loading ? "Updating..." : "Update Holiday"}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

// Bulk Add Holidays Offcanvas
const BulkAddHolidaysOffcanvas: React.FC<{
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    stateCircles: StateCircle[]
    districts: District[]
    departments: Department[]
}> = ({ isOpen, onClose, onSuccess, stateCircles, districts, departments }) => {
    const [holidays, setHolidays] = useState<
        Array<{
            holiday_date: string
            holiday_name: string
            holiday_type: string
            description: string
            is_nationwide: boolean
            state_circle_id: string
            district_id: string
            department_ids: number[]
        }>
    >([
        {
            holiday_date: "",
            holiday_name: "",
            holiday_type: "public_holiday",
            description: "",
            is_nationwide: true,
            state_circle_id: "",
            district_id: "",
            department_ids: [],
        },
    ])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    const addHolidayRow = () => {
        setHolidays([
            ...holidays,
            {
                holiday_date: "",
                holiday_name: "",
                holiday_type: "public_holiday",
                description: "",
                is_nationwide: true,
                state_circle_id: "",
                district_id: "",
                department_ids: [],
            },
        ])
    }

    const removeHolidayRow = (index: number) => {
        setHolidays(holidays.filter((_, i) => i !== index))
    }

    const updateHoliday = (index: number, field: string, value: any) => {
        const updated = [...holidays]
        updated[index] = { ...updated[index], [field]: value }
        setHolidays(updated)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        try {
            setLoading(true)
            setError("")

            const payload = {
                holidays: holidays.map((h) => {
                    const item: any = {
                        holiday_date: h.holiday_date,
                        holiday_name: h.holiday_name,
                        holiday_type: h.holiday_type,
                        description: h.description || undefined,
                        is_nationwide: h.department_ids.length ? false : h.is_nationwide,
                    }
                    if (h.department_ids.length) {
                        item.department_ids = h.department_ids
                    }

                    if (!h.is_nationwide) {
                        if (h.state_circle_id) {
                            item.state_circle_id = Number.parseInt(h.state_circle_id)
                        }
                        if (h.district_id) {
                            item.district_id = Number.parseInt(h.district_id)
                        }
                    }

                    return item
                }),
            }

            await axiosInstance.post("/calendar/bulk-holidays", payload)
            onSuccess()
            onClose()
            setHolidays([
                {
                    holiday_date: "",
                    holiday_name: "",
                    holiday_type: "public_holiday",
                    description: "",
                    is_nationwide: true,
                    state_circle_id: "",
                    district_id: "",
                    department_ids: [],
                },
            ])
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to add holidays")
        } finally {
            setLoading(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
            <div className="absolute right-0 top-0 h-full w-full sm:max-w-3xl bg-white shadow-xl overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Bulk Add Holidays</h2>
                        <p className="text-sm text-gray-600 mt-1">Add multiple holidays at once</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <FiX className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="p-4">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
                            <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-red-700">{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {holidays.map((holiday, index) => (
                            <div key={index} className="border border-gray-200 rounded p-4 relative">
                                {holidays.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeHolidayRow(index)}
                                        className="absolute top-2 right-2 p-1 text-red-600 hover:bg-red-50 rounded"
                                    >
                                        <FiX className="w-4 h-4" />
                                    </button>
                                )}

                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
                                            <input
                                                type="text"
                                                value={holiday.holiday_name}
                                                onChange={(e) => updateHoliday(index, "holiday_name", e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                                required
                                                minLength={3}
                                                maxLength={200}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                                            <input
                                                type="date"
                                                value={holiday.holiday_date}
                                                onChange={(e) => updateHoliday(index, "holiday_date", e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                                        <select
                                            value={holiday.holiday_type}
                                            onChange={(e) => updateHoliday(index, "holiday_type", e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                            required
                                        >
                                            <option value="public_holiday">Public Holiday</option>
                                            <option value="festival">Festival</option>
                                            <option value="company_holiday">Company Holiday</option>
                                            <option value="optional_holiday">Optional Holiday</option>
                                            <option value="restricted_holiday">Restricted Holiday</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <input
                                            type="text"
                                            value={holiday.description}
                                            onChange={(e) => updateHoliday(index, "description", e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                            maxLength={1000}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Departments</label>
                                        <DepartmentMultiSelect
                                            departments={departments}
                                            value={holiday.department_ids}
                                            onChange={(ids) => updateHoliday(index, "department_ids", ids)}
                                        />
                                    </div>

                                    {/* <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`nationwide_${index}`}
                                            checked={holiday.is_nationwide}
                                            onChange={(e) => updateHoliday(index, 'is_nationwide', e.target.checked)}
                                            className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor={`nationwide_${index}`} className="ml-2 text-sm text-gray-700">
                                            Nationwide
                                        </label>
                                    </div> */}
                                </div>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={addHolidayRow}
                            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded hover:border-blue-500 hover:bg-blue-50 transition-colors text-sm font-medium text-gray-600 hover:text-blue-800 flex items-center justify-center gap-2"
                        >
                            <FiPlus className="w-4 h-4" />
                            Add Another Holiday
                        </button>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 transition-colors text-sm font-medium disabled:opacity-50"
                            >
                                {loading ? "Adding..." : `Add ${holidays.length} Holiday${holidays.length > 1 ? "s" : ""}`}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

// Delete Confirmation Modal
const DeleteConfirmationModal: React.FC<{
    isOpen: boolean
    onClose: () => void
    holiday: Holiday | null
    onConfirm: () => void
    loading: boolean
}> = ({ isOpen, onClose, holiday, onConfirm, loading }) => {
    if (!isOpen || !holiday) return null

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

                <div className="relative bg-white rounded shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                            <FiTrash2 className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Delete Holiday</h3>
                            <p className="text-sm text-gray-600">This action cannot be undone</p>
                        </div>
                    </div>

                    <div className="mb-6 p-4 bg-gray-50 rounded">
                        <p className="text-sm text-gray-700">
                            Are you sure you want to delete <strong>{holiday.holiday_name}</strong>?
                        </p>
                        <p className="text-xs text-gray-600 mt-2">Date: {new Date(holiday.holiday_date).toLocaleDateString()}</p>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                            {loading ? "Deleting..." : "Delete"}
                        </button>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

// Main Component
const HolidayManagement: React.FC = () => {
    const [viewMode, setViewMode] = useState<"list" | "calendar">("list")
    const [currentDate, setCurrentDate] = useState(new Date())
    const [holidays, setHolidays] = useState<Holiday[]>([])
    const [stateCircles, setStateCircles] = useState<StateCircle[]>([])
    const [districts, setDistricts] = useState<District[]>([])
    const [departments, setDepartments] = useState<Department[]>([])
    const [loading, setLoading] = useState(false)
    const [filters, setFilters] = useState({
        year: new Date().getFullYear(),
        month: "",
        holiday_type: "",
        is_nationwide: "",
        state_circle_id: "",
        is_active: "",
        // Comma-separated department ids
        department_id: "",
    })
    const [pagination, setPagination] = useState<PaginationType>({
        page: 1,
        limit: 10, // Changed default limit from 50 to 10
        total_count: 0,
        total_pages: 0,
        offset: 0,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    })
    const [sortConfig, setSortConfig] = useState<HolidaySortConfig>({
        field: "holiday_date",
        direction: "ASC",
    })

    // Modal states
    const [addOffcanvasOpen, setAddOffcanvasOpen] = useState(false)
    const [editOffcanvasOpen, setEditOffcanvasOpen] = useState(false)
    const [bulkOffcanvasOpen, setBulkOffcanvasOpen] = useState(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [showExcelModal, setShowExcelModal] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [exportData, setExportData] = useState<HolidayExportRow[]>([])

    useEffect(() => {
        fetchHolidays()
        // fetchStateCircles();
        // fetchDistricts();
    }, [currentDate, filters, pagination.page, viewMode]) // removed pagination.limit from dependencies

    useEffect(() => {
        fetchDepartments()
    }, [])

    const fetchHolidays = async (
        limitValue?: number,
        pageValue?: number,
        sort: HolidaySortConfig = sortConfig
    ) => {
        try {
            setLoading(true)
            const effectiveLimit = limitValue ?? pagination.limit ?? 10
            const effectivePage = pageValue ?? pagination.page
            const params: any = {
                page: effectivePage,
                limit: effectiveLimit,
            }

            if (viewMode === "calendar") {
                params.year = currentDate.getFullYear()
                params.month = currentDate.getMonth() + 1
            } else {
                if (filters.year) params.year = filters.year
                if (filters.month) params.month = filters.month
            }

            if (filters.holiday_type) params.holiday_type = filters.holiday_type
            if (filters.is_nationwide !== "") params.is_nationwide = filters.is_nationwide === "true"
            if (filters.state_circle_id) params.state_circle_id = filters.state_circle_id
            if (filters.is_active !== "") params.is_active = filters.is_active === "true"
            if (filters.department_id) params.department_id = filters.department_id
            if (sort?.field) {
                params.sort_by = sort.field
                params.sort_order = sort.direction
            }

            const response = await axiosInstance.get("/calendar/holidays", { params })
            setHolidays(response.data.data.holidays || [])
            if (response.data.data.pagination) {
                setPagination((prev) => ({
                    ...prev,
                    page: effectivePage,
                    limit: effectiveLimit,
                    total_count: response.data.data.pagination.total_count,
                    total_pages: response.data.data.pagination.total_pages,
                }))
            }
        } catch (error) {
            console.error("Error fetching holidays:", error)
        } finally {
            setLoading(false)
        }
    }

    const fetchStateCircles = async () => {
        try {
            const response = await axiosInstance.get("/state-circles")
            setStateCircles(response.data.data.stateCircles || [])
        } catch (error) {
            console.error("Error fetching state circles:", error)
        }
    }

    const fetchDistricts = async () => {
        try {
            const response = await axiosInstance.get("/districts")
            setDistricts(response.data.data.districts || [])
        } catch (error) {
            console.error("Error fetching districts:", error)
        }
    }

    const fetchDepartments = async () => {
        try {
            const response = await axiosInstance.get("/departments/list", { params: { limit: 500, sort_by: "name", sort_order: "ASC" } })
            setDepartments(response.data.data.departments || [])
        } catch (error) {
            console.error("Error fetching departments:", error)
        }
    }

    const handleDelete = async () => {
        if (!selectedHoliday) return

        try {
            setDeleteLoading(true)
            await axiosInstance.delete(`/calendar/holiday/${selectedHoliday.id}`)
            fetchHolidays()
            setDeleteModalOpen(false)
            setSelectedHoliday(null)
        } catch (error) {
            console.error("Error deleting holiday:", error)
        } finally {
            setDeleteLoading(false)
        }
    }

    const handlePageChange = (page: number) => {
        setPagination((prev) => ({ ...prev, page }))
    }

    const handleLimitChange = (limit: number) => {
        setPagination((prev) => ({ ...prev, limit, page: 1 }))
        fetchHolidays(limit, 1, sortConfig)
    }

    const handleSort = (field: string) => {
        const direction = sortConfig.field === field && sortConfig.direction === "ASC" ? "DESC" : "ASC"
        const newSort = { field, direction } as HolidaySortConfig
        setSortConfig(newSort)
        fetchHolidays(pagination.limit, pagination.page, newSort)
    }

    const getSortIcon = (field: string) => {
        if (sortConfig.field !== field) return <FaSort className="w-3 h-3 text-gray-400" />
        return sortConfig.direction === "ASC" ? (
            <FaSortUp className="w-3 h-3 text-blue-800" />
        ) : (
            <FaSortDown className="w-3 h-3 text-blue-800" />
        )
    }

    const formatDateValue = (value?: string | null, options?: Intl.DateTimeFormatOptions) => {
        if (!value) return ""
        try {
            return new Date(value).toLocaleDateString(
                "en-IN",
                options ?? { day: "2-digit", month: "short", year: "numeric" }
            )
        } catch (error) {
            console.error("Error formatting date:", error)
            return value
        }
    }

    const formatDateTimeValue = (value?: string | null) => {
        if (!value) return ""
        try {
            return new Date(value).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            })
        } catch (error) {
            console.error("Error formatting datetime:", error)
            return value
        }
    }

    const getWeekdayLabel = (value?: string | null) => {
        if (!value) return ""
        try {
            return new Date(value).toLocaleDateString("en-US", { weekday: "long" })
        } catch (error) {
            console.error("Error formatting weekday:", error)
            return ""
        }
    }

    const getMonthLabel = (value?: string | null) => {
        if (!value) return ""
        try {
            return new Date(value).toLocaleDateString("en-US", { month: "long" })
        } catch (error) {
            console.error("Error formatting month:", error)
            return ""
        }
    }

    const getHolidayTypeLabel = (type?: string) => {
        if (!type) return ""
        return type
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
    }

    const getCreatorName = (creator?: Holiday["creator"]) => {
        if (!creator) return "N/A"
        return [creator.first_name, creator.last_name].filter(Boolean).join(" ") || "N/A"
    }

    const buildExportParams = (
        page: number,
        limit: number,
        sort: HolidaySortConfig = sortConfig
    ): Record<string, any> => {
        const params: Record<string, any> = {
            page,
            limit,
        }

        if (viewMode === "calendar") {
            params.year = currentDate.getFullYear()
            params.month = currentDate.getMonth() + 1
        } else {
            if (filters.year) params.year = filters.year
            if (filters.month) params.month = filters.month
        }

        if (filters.holiday_type) params.holiday_type = filters.holiday_type
        if (filters.is_nationwide !== "") params.is_nationwide = filters.is_nationwide === "true"
        if (filters.state_circle_id) params.state_circle_id = filters.state_circle_id
        if (filters.is_active !== "") params.is_active = filters.is_active === "true"
        if (filters.department_id) params.department_id = filters.department_id

        if (sort?.field) {
            params.sort_by = sort.field
            params.sort_order = sort.direction
        }

        return params
    }

    const mapHolidayForExport = (holiday: Holiday): HolidayExportRow => {
        return {
            "Holiday Date": formatDateValue(holiday.holiday_date),
            Weekday: getWeekdayLabel(holiday.holiday_date),
            Month: getMonthLabel(holiday.holiday_date),
            "Holiday Name": holiday.holiday_name,
            "Holiday Type": getHolidayTypeLabel(holiday.holiday_type),
            Description: holiday.description || "",
            Year: holiday.year || "",
            Scope: holiday.is_nationwide ? "Nationwide" : "Regional",
            "Nationwide": holiday.is_nationwide ? "Yes" : "No",
            State: holiday.state_circle?.state_name || "",
            Circle: holiday.state_circle?.circle_name || "",
            District: holiday.district?.district_name || "",
            Departments: departmentNames(holiday),
            Status: holiday.is_active ? "Active" : "Inactive",
            "Created On": formatDateTimeValue(holiday.created_at),
            "Updated On": formatDateTimeValue(holiday.updated_at),
            "Created By": getCreatorName(holiday.creator),
        }
    }

    const fetchExportData = async () => {
        if (exporting) return

        try {
            setExporting(true)
            setExportData([])
            const pageSize = 100
            let page = 1
            let hasMore = true
            const allHolidays: Holiday[] = []

            while (hasMore) {
                const params = buildExportParams(page, pageSize, sortConfig)
                const response = await axiosInstance.get("/calendar/holidays", { params })
                const data = response.data?.data || {}
                const fetched: Holiday[] = data.holidays || []
                const paginationData = data.pagination

                allHolidays.push(...fetched)

                const nextPageAvailable = paginationData?.has_next_page ??
                    (paginationData?.total_pages ? page < paginationData.total_pages : fetched.length === pageSize)

                if (!fetched.length || !nextPageAvailable) {
                    hasMore = false
                } else {
                    page += 1
                }
            }

            if (!allHolidays.length) {
                toast.error("No holidays found for export")
                return
            }

            setExportData(allHolidays.map(mapHolidayForExport))
            setShowExcelModal(true)
        } catch (error: any) {
            console.error("Error preparing holiday export:", error)
            toast.error(error.response?.data?.message || "Failed to prepare export data")
        } finally {
            setExporting(false)
        }
    }

    const getCalendarDays = (): CalendarDay[] => {
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()

        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        const daysInMonth = lastDay.getDate()
        const startingDayOfWeek = firstDay.getDay()

        const days: CalendarDay[] = []

        const prevMonthLastDay = new Date(year, month, 0).getDate()
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                date: new Date(year, month - 1, prevMonthLastDay - i),
                isCurrentMonth: false,
                holidays: [],
            })
        }

        for (let i = 1; i <= daysInMonth; i++) {
            const date = new Date(year, month, i)
            const dateString = date.toISOString().split("T")[0]
            const dayHolidays = holidays.filter((h) => h.holiday_date === dateString)

            days.push({
                date,
                isCurrentMonth: true,
                holidays: dayHolidays,
            })
        }

        const remainingDays = 42 - days.length
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                date: new Date(year, month + 1, i),
                isCurrentMonth: false,
                holidays: [],
            })
        }

        return days
    }

    const getHolidayTypeBadge = (type: string) => {
        const config: Record<string, { bg: string; text: string }> = {
            public_holiday: { bg: "bg-blue-100", text: "text-blue-900" },
            festival: { bg: "bg-purple-100", text: "text-purple-700" },
            company_holiday: { bg: "bg-green-100", text: "text-green-700" },
            optional_holiday: { bg: "bg-yellow-100", text: "text-yellow-700" },
            restricted_holiday: { bg: "bg-red-100", text: "text-red-700" },
        }
        const c = config[type] || config.public_holiday
        return (
            <span className={`px-2 capitalize py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                {type.replace("_", " ")}
            </span>
        )
    }

    const CalendarView = () => {
        const calendarDays = getCalendarDays()
        const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

        return (
            <div className="bg-white rounded shadow-sm border border-gray-200">
                <div className="p-3 sm:p-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                            {currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                        </h3>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                                className="p-2 hover:bg-gray-100 rounded transition-colors"
                            >
                                <FiChevronLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            <button
                                onClick={() => setCurrentDate(new Date())}
                                className="px-3 py-1 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors"
                            >
                                Today
                            </button>
                            <button
                                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                                className="p-2 hover:bg-gray-100 rounded transition-colors"
                            >
                                <FiChevronRight className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-2 sm:p-4">
                    <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                        {weekDays.map((day) => (
                            <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
                                <span className="hidden sm:inline">{day}</span>
                                <span className="sm:hidden">{day.substring(0, 1)}</span>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1 sm:gap-2">
                        {calendarDays.map((day, index) => {
                            const isToday = day.date.toDateString() === new Date().toDateString()
                            const isSunday = day.date.getDay() === 0
                            const hasHoliday = day.holidays.length > 0

                            return (
                                <div
                                    key={index}
                                    className={`min-h-[60px] sm:min-h-[100px] border rounded p-1 sm:p-2 transition-all ${day.isCurrentMonth ? "bg-white" : "bg-gray-50"
                                        } ${isToday ? "border-blue-500 border-2" : "border-gray-200"} ${isSunday && day.isCurrentMonth ? "bg-red-50" : ""
                                        } ${hasHoliday && day.isCurrentMonth ? "border-purple-300 bg-purple-50" : ""}`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span
                                            className={`text-xs sm:text-sm font-medium ${day.isCurrentMonth ? "text-gray-900" : "text-gray-400"
                                                } ${isToday ? "text-blue-800 font-bold" : ""}`}
                                        >
                                            {day.date.getDate()}
                                        </span>
                                    </div>

                                    {day.isCurrentMonth && hasHoliday && (
                                        <div className="space-y-1">
                                            {day.holidays.slice(0, 2).map((holiday, idx) => (
                                                <div
                                                    key={idx}
                                                    className="text-xs truncate text-purple-700 font-medium bg-purple-100 px-1 py-0.5 rounded"
                                                    title={holiday.holiday_name}
                                                >
                                                    {holiday.holiday_name}
                                                </div>
                                            ))}
                                            {day.holidays.length > 2 && (
                                                <div className="text-xs text-purple-600 font-medium">+{day.holidays.length - 2} more</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        )
    }

    const ListView = () => (
        <div className="bg-white rounded shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Actions</th>
                            <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                                <button
                                    type="button"
                                    onClick={() => handleSort("holiday_date")}
                                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                                >
                                    Date
                                    {getSortIcon("holiday_date")}
                                </button>
                            </th>
                            <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                                <button
                                    type="button"
                                    onClick={() => handleSort("holiday_name")}
                                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                                >
                                    Holiday Name
                                    {getSortIcon("holiday_name")}
                                </button>
                            </th>
                            <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                                <button
                                    type="button"
                                    onClick={() => handleSort("holiday_type")}
                                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                                >
                                    Type
                                    {getSortIcon("holiday_type")}
                                </button>
                            </th>
                            {/* <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase hidden md:table-cell">Scope</th> */}
                            <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase min-w-[180px]">Departments</th>
                            <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase hidden lg:table-cell">
                                <button
                                    type="button"
                                    onClick={() => handleSort("description")}
                                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                                >
                                    Description
                                    {getSortIcon("description")}
                                </button>
                            </th>
                            <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                                <button
                                    type="button"
                                    onClick={() => handleSort("is_active")}
                                    className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold"
                                >
                                    Status
                                    {getSortIcon("is_active")}
                                </button>
                            </th>
                            {/* <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Actions</th> */}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-12 text-center">
                                    <div className="flex flex-col items-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-3"></div>
                                        <span className="text-sm text-gray-600">Loading holidays...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : holidays.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-12 text-center">
                                    <FiCalendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                    <p className="text-sm text-gray-500">No holidays found</p>
                                </td>
                            </tr>
                        ) : (
                            holidays.map((holiday) => (
                                <tr key={holiday.id} className="hover:bg-gray-50">
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedHoliday(holiday)
                                                    setEditOffcanvasOpen(true)
                                                }}
                                                className="text-blue-800 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                                                title="Edit"
                                            >
                                                <FiEdit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedHoliday(holiday)
                                                    setDeleteModalOpen(true)
                                                }}
                                                className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                                                title="Delete"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-xs sm:text-sm">
                                        {new Date(holiday.holiday_date).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                            year: "numeric",
                                        })}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{holiday.holiday_name}</div>
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">{getHolidayTypeBadge(holiday.holiday_type)}</td>
                                    {/* <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-sm hidden md:table-cell">
                                        {holiday.is_nationwide ? (
                                            <span className="text-green-600 font-medium">Nationwide</span>
                                        ) : (
                                            <span className="text-gray-600">
                                                {holiday.state_circle?.state_name || 'Regional'}
                                            </span>
                                        )}
                                    </td> */}
                                    <td className="px-3 sm:px-4 py-3">
                                        {holiday.departments?.length ? (
                                            <div className="flex flex-wrap gap-1 max-w-[280px]">
                                                {holiday.departments.map((d) => (
                                                    <span
                                                        key={d.id}
                                                        className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800"
                                                    >
                                                        {d.name}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-500 italic">All Departments</span>
                                        )}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 hidden lg:table-cell">
                                        <div className="text-sm text-gray-600 truncate max-w-[200px]">{holiday.description || "-"}</div>
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                                        <span
                                            className={`px-2 py-1 rounded-full text-xs font-medium ${holiday.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                                                }`}
                                        >
                                            {holiday.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </td>
                                    {/*
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedHoliday(holiday)
                                                    setEditOffcanvasOpen(true)
                                                }}
                                                className="text-blue-800 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                                                title="Edit"
                                            >
                                                <FiEdit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedHoliday(holiday)
                                                    setDeleteModalOpen(true)
                                                }}
                                                className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                                                title="Delete"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                    */}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {viewMode === "list" && (
                <PaginationUtils pagination={pagination} onPageChange={handlePageChange} onLimitChange={handleLimitChange} />
            )}
        </div>
    )

    return (
        <>
            <div className="space-y-4 sm:space-y-6">
                {/* Header */}
                <div className="bg-white rounded shadow-sm border border-gray-200 p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Holiday Calendar</h1>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1">Manage company holidays and events</p>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <button
                                onClick={() => setAddOffcanvasOpen(true)}
                                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                            >
                                <FiPlus className="w-4 h-4" />
                                Add Holiday
                            </button>
                            <button
                                onClick={() => setBulkOffcanvasOpen(true)}
                                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                            >
                                <FiUpload className="w-4 h-4" />
                                Bulk Add
                            </button>
                            <button
                                onClick={fetchExportData}
                                disabled={exporting}
                                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:bg-green-400 disabled:cursor-not-allowed"
                            >
                                {exporting ? (
                                    <>
                                        <FaSpinner className="w-4 h-4 animate-spin" />
                                        Preparing...
                                    </>
                                ) : (
                                    <>
                                        <FaFileExcel className="w-4 h-4" />
                                        Export
                                    </>
                                )}
                            </button>
                            <div className="flex items-center gap-1 bg-gray-100 rounded p-1">
                                <button
                                    onClick={() => setViewMode("list")}
                                    className={`flex items-center gap-2 px-2 sm:px-3 py-2 rounded text-sm font-medium transition-colors ${viewMode === "list" ? "bg-white text-blue-800 shadow-sm" : "text-gray-600"
                                        }`}
                                >
                                    <FiList className="w-4 h-4" />
                                    <span className="hidden sm:inline">List</span>
                                </button>
                                <button
                                    onClick={() => setViewMode("calendar")}
                                    className={`flex items-center gap-2 px-2 sm:px-3 py-2 rounded text-sm font-medium transition-colors ${viewMode === "calendar" ? "bg-white text-blue-800 shadow-sm" : "text-gray-600"
                                        }`}
                                >
                                    <FiCalendar className="w-4 h-4" />
                                    <span className="hidden sm:inline">Calendar</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters (Only for List View) */}
                {viewMode === "list" && (
                    <div className="bg-white rounded shadow-sm border border-gray-200 p-3 sm:p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                                <select
                                    value={filters.year}
                                    onChange={(e) => setFilters({ ...filters, year: Number.parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                    <option value="">All Years</option>
                                    {Array.from({ length: 11 }, (_, i) => {
                                        const currentYear = new Date().getFullYear()
                                        const year = currentYear - 5 + i
                                        return (
                                            <option key={year} value={year}>
                                                {year}
                                                {year === currentYear ? " (Current)" : ""}
                                            </option>
                                        )
                                    })}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                                <select
                                    value={filters.month}
                                    onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                    <option value="">All Months</option>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {new Date(2000, i).toLocaleDateString("en-US", { month: "long" })}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <select
                                    value={filters.holiday_type}
                                    onChange={(e) => setFilters({ ...filters, holiday_type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                    <option value="">All Types</option>
                                    <option value="public_holiday">Public Holiday</option>
                                    <option value="festival">Festival</option>
                                    <option value="company_holiday">Company Holiday</option>
                                    <option value="optional_holiday">Optional Holiday</option>
                                    <option value="restricted_holiday">Restricted Holiday</option>
                                </select>
                            </div>

                            <div className="lg:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Departments</label>
                                <DepartmentMultiSelect
                                    departments={departments}
                                    value={filters.department_id ? filters.department_id.split(",").map(Number) : []}
                                    onChange={(ids) => {
                                        setFilters({ ...filters, department_id: ids.join(",") })
                                        setPagination((prev) => ({ ...prev, page: 1 }))
                                    }}
                                    hint={false}
                                />
                            </div>

                            {/* <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                                <select
                                    value={filters.is_nationwide}
                                    onChange={(e) => setFilters({ ...filters, is_nationwide: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                    <option value="">All</option>
                                    <option value="true">Nationwide</option>
                                    <option value="false">Regional</option>
                                </select>
                            </div> */}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    value={filters.is_active}
                                    onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                    <option value="">All</option>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>

                            {/* <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Actions</label>
                                <button
                                    onClick={() => fetchHolidays(pagination.limit)}
                                    className="w-full px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                                >
                                    <FiFilter className="w-4 h-4" />
                                    Apply
                                </button>
                            </div> */}
                        </div>
                    </div>
                )}

                {/* Content */}
                {viewMode === "calendar" ? <CalendarView /> : <ListView />}
            </div>

            {/* Modals & Offcanvas */}
            <AddHolidayOffcanvas
                isOpen={addOffcanvasOpen}
                onClose={() => setAddOffcanvasOpen(false)}
                onSuccess={fetchHolidays}
                stateCircles={stateCircles}
                districts={districts}
                departments={departments}
            />

            <EditHolidayOffcanvas
                isOpen={editOffcanvasOpen}
                onClose={() => setEditOffcanvasOpen(false)}
                holiday={selectedHoliday}
                onSuccess={fetchHolidays}
                stateCircles={stateCircles}
                districts={districts}
                departments={departments}
            />

            <BulkAddHolidaysOffcanvas
                isOpen={bulkOffcanvasOpen}
                onClose={() => setBulkOffcanvasOpen(false)}
                onSuccess={fetchHolidays}
                stateCircles={stateCircles}
                districts={districts}
                departments={departments}
            />

            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                holiday={selectedHoliday}
                onConfirm={handleDelete}
                loading={deleteLoading}
            />

            <ExcelDownload
                isOpen={showExcelModal}
                onClose={() => setShowExcelModal(false)}
                data={exportData}
                filename="holiday_management"
                title="Export Holidays Data"
                description="Download comprehensive holiday details"
            />
        </>
    )
}

export default HolidayManagement
